export class ProductMedia extends window.BAO.Carousel.Element {
  static get requiredElements () {
    return super.requiredElements.concat([
      'thumbs',
      'thumb',
      'thumbs-next',
      'thumbs-previous',
      'thumbs-fade-top',
      'thumbs-fade-bottom',
      'next',
      'previous',
    ])
  }

  initialiseCarousel () {
    const initialSlide = parseInt(this.dataset.initialSlide, 10)

    this.thumbsCarousel = new window.KeenSlider(
      this.els.thumbs.element,
      {
        selector: this.els.thumb.selector,
        initial: initialSlide,
        slides: {
          perView: 'auto',
          spacing: 5,
        },
        breakpoints: {
          [`(min-width: ${window.BAO.MediaBreakpoints.breakpoints.md +
            1}px)`]: {
            vertical: true,
            slides: {
              perView: 'auto',
              spacing: 5,
            },
          },
        },
      },
      [
        window.BAO.Carousel.useInitialised(),
        window.BAO.Carousel.useArrows({
          next: this.els['thumbs-next'].element,
          previous: this.els['thumbs-previous'].element,
        }),
        this.useThumbsScrollFade(),
      ],
    )

    return new window.KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        defaultAnimation: {
          duration: 1500,
        },
        loop: false,
        mode: 'snap',
        initial: initialSlide,
        slides: {
          // 92% slide width shows an 8% peek of the next image
          perView: 1 / 0.92,
          spacing: 0,
        },
        breakpoints: {
          [`(min-width: ${window.BAO.MediaBreakpoints.breakpoints.md +
            1}px)`]: {
            slides: {
              perView: 1,
              spacing: 0,
            },
          },
        },
      },
      [
        window.BAO.Carousel.useInitialised(),
        window.BAO.Carousel.useDots(this.els.thumb.elements),
        window.BAO.Carousel.useArrows({
          next: this.els.next.element,
          previous: this.els.previous.element,
        }),
        carousel => {
          // Center on the animation's final target index rather than
          // reacting to `slideChanged`, which fires for every intermediate
          // slide the animated transition passes through on its way there.
          const centerThumbForCurrentSlide = () => {
            const index =
              carousel.animator.targetIdx ?? carousel.track.details.rel

            this.centerActiveThumb(index)
          }

          carousel.on('created', centerThumbForCurrentSlide)
          carousel.on('animationStarted', centerThumbForCurrentSlide)
        },
      ],
    )
  }

  centerActiveThumb (index) {
    const carousel = this.thumbsCarousel
    const { details } = carousel.track
    const slide = details && details.slides[index]

    if (!slide) {
      carousel.moveToIdx(index)
      return
    }

    // Jump to a computed position rather than moveToIdx() so only the active
    // thumb is centered, instead of setting a global slide `origin` that
    // centers every thumb (including the first on load, before any scroll).
    const centeredDistance = 0.5 - slide.size / 2
    const delta = slide.distance - centeredDistance
    const target = Math.min(
      Math.max(details.position + delta, details.min),
      details.max,
    )

    carousel.animator.start([
      {
        distance: target - details.position,
        duration: 500,
        easing: t => 1 + --t * t * t * t * t,
      },
    ])
  }

  useThumbsScrollFade () {
    const top = this.els['thumbs-fade-top'].element
    const bottom = this.els['thumbs-fade-bottom'].element

    /** @param {import('keen-slider').KeenSliderInstance} carousel */
    return carousel => {
      const updateFadeStates = () => {
        const { abs, maxIdx, min } = carousel.track.details

        top.dataset.visible = (abs > min).toString()
        bottom.dataset.visible = (abs < maxIdx).toString()
      }

      carousel.on('created', updateFadeStates)
      carousel.on('slideChanged', updateFadeStates)
    }
  }
}

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia)
}
